import { Icon } from '../components/Icon';
import { useEngineStore } from '../state/engineStore';
import type { ChtLimits, CylinderCount, Units } from '../types/engine';

export function SettingsScreen() {
  const { config, setCylinderCount, setChtLimits, setUnits } = useEngineStore();

  const updateLimit = (key: keyof ChtLimits, value: number) => {
    setChtLimits({ ...config.chtLimits, [key]: value });
  };

  return (
    <div className="col gap-lg">
      <div className="card card-lg">
        <div className="card-h" style={{ marginBottom: 4 }}>
          Engine configuration
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 20 }}>
          Applies to all screens in this session. Changes are not persisted.
        </div>
        <SegmentedToggle<CylinderCount>
          label="Cylinder count"
          value={config.cylinderCount}
          options={[
            { value: 4, label: '4 cylinders' },
            { value: 6, label: '6 cylinders' },
          ]}
          onChange={setCylinderCount}
        />
        <div style={{ height: 16 }} />
        <SegmentedToggle<Units>
          label="Units"
          value={config.units}
          options={[
            { value: 'imperial', label: '°F / GPH / kts' },
            { value: 'metric', label: '°C / L·h⁻¹ / km/h' },
          ]}
          onChange={setUnits}
        />
      </div>

      <div className="card card-lg">
        <div className="card-h" style={{ marginBottom: 4 }}>
          Personal CHT limits
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 20 }}>
          Manufacturer red line is often too high. These are your operational thresholds.
        </div>
        <div className="col">
          <LimitRow
            label="Comfort zone"
            value={config.chtLimits.ok}
            min={300}
            max={420}
            color="var(--ok)"
            onChange={(v) => updateLimit('ok', v)}
          />
          <LimitRow
            label="Yellow band"
            value={config.chtLimits.warn}
            min={300}
            max={420}
            color="var(--warn)"
            onChange={(v) => updateLimit('warn', v)}
          />
          <LimitRow
            label="Personal red line"
            value={config.chtLimits.danger}
            min={380}
            max={460}
            color="var(--danger)"
            onChange={(v) => updateLimit('danger', v)}
          />
        </div>
      </div>

      <div className="callout info">
        <Icon name="spark" size={18} />
        <div>
          <div className="callout-title">Advisory only</div>
          <div>
            Lean Assist is not a certified instrument. All readings are pilot-entered and
            unverified. Cross-check with your engine monitor before acting on anything the
            app displays.
          </div>
        </div>
      </div>
    </div>
  );
}

type SegmentedToggleProps<T extends string | number> = {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
};

function SegmentedToggle<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: SegmentedToggleProps<T>) {
  return (
    <div>
      <div className="stat-label" style={{ marginBottom: 8 }}>
        {label}
      </div>
      <div
        role="tablist"
        aria-label={label}
        className="segmented-toggle"
      >
        {options.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            role="tab"
            aria-selected={value === opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              background: value === opt.value ? 'var(--surface)' : 'transparent',
              color: value === opt.value ? 'var(--ink)' : 'var(--ink-2)',
              fontSize: 14,
              fontWeight: value === opt.value ? 600 : 500,
              boxShadow: value === opt.value ? 'var(--shadow-sm)' : 'none',
              fontFamily: 'inherit',
              minHeight: 44,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

type LimitRowProps = {
  label: string;
  value: number;
  min: number;
  max: number;
  color: string;
  onChange: (value: number) => void;
};

function LimitRow({ label, value, min, max, color, onChange }: LimitRowProps) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid var(--line-2)' }}>
      <div className="row between" style={{ marginBottom: 4 }}>
        <div className="row">
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: color,
            }}
          />
          <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
        </div>
        <div className="num-md" style={{ color }}>
          ≤ {value}
          <span className="unit">°F</span>
        </div>
      </div>
      <div className="limit-slider-track" style={{ ['--thumb-color' as string]: color }}>
        <div className="limit-slider-fill" style={{ width: `${pct}%` }} />
        <input
          type="range"
          className="limit-slider"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={label}
          style={{ position: 'relative', zIndex: 1 }}
        />
      </div>
    </div>
  );
}
